import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Modal } from '../ui';
import {
  addComment,
  archiveApplication,
  decideApplication,
  deleteApplication,
  getACCAttachmentUrl,
  getApplication,
  listACCAttachments,
  listExternalEmailNotifications,
  retryExternalEmailNotification,
} from '../../services/accApplicationsService';

const statusStyles = {
  pending: 'bg-amber-50 text-amber-800 border border-amber-200',
  accepted: 'bg-emerald-50 text-emerald-800 border border-emerald-200',
  denied: 'bg-rose-50 text-rose-800 border border-rose-200',
  archived: 'bg-slate-100 text-slate-500 border border-slate-200',
};

const statusLabels = {
  pending: 'Pending',
  accepted: 'Accepted',
  denied: 'Denied',
  archived: 'Archived',
};

const MaterialIcon = ({ name, size = 20, className = '' }) => (
  <span className={`material-symbols-outlined ${className}`} style={{ fontSize: `${size}px` }}>{name}</span>
);

const StatusPill = ({ status }) => (
  <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ${statusStyles[status] || statusStyles.pending}`}>
    {statusLabels[status] || status}
  </span>
);

const formatDateTime = (value) => {
  if (!value) return 'Not recorded';
  return new Date(value).toLocaleString('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const formatRelative = (value) => {
  if (!value) return '';
  const diffMs = Date.now() - new Date(value).getTime();
  const diffMinutes = Math.max(1, Math.round(diffMs / 60000));
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hr ago`;
  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 14) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const emailEventLabels = {
  submission_received: 'Confirmation',
  decision_accepted: 'Decision (Accepted)',
  decision_denied: 'Decision (Denied)',
};

const wait = (milliseconds) => new Promise((resolve) => {
  setTimeout(resolve, milliseconds);
});

// Which viewer to use for an attachment's inline preview. Images and PDFs render
// in-browser; Office docs and everything else fall back to a download prompt.
const getPreviewKind = (attachment) => {
  const mime = (attachment?.mime_type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf') return 'pdf';
  return 'other';
};

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ACCApplicationDetailModal = ({ applicationId, isOpen, onClose, onChanged, onDeleted, initialApplication = null }) => {
  const { user } = useAuth();
  const [application, setApplication] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentBody, setCommentBody] = useState('');
  const [decisionNote, setDecisionNote] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState([]);
  const [retryingEmailId, setRetryingEmailId] = useState(null);
  const [error, setError] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const role = user?.portalRole || 'member';
  const isAdmin = role === 'admin';
  const isManagement = role === 'management';
  const isACCMember = !!user?.is_acc_member;

  const permissions = useMemo(() => {
    const canSeeComments = isAdmin || isManagement || isACCMember;
    return {
      canSeeComments,
      canComment: canSeeComments && application?.status !== 'archived',
      canDecide: (isAdmin || isManagement || isACCMember) && application?.status === 'pending',
      canArchive: (isAdmin || isManagement || isACCMember) && ['accepted', 'denied'].includes(application?.status),
      canDelete: isAdmin,
      canRetryEmail: isAdmin || isManagement,
    };
  }, [application?.status, isACCMember, isAdmin, isManagement]);

  const latestEmailNotifications = useMemo(() => {
    const seen = new Set();
    return (emailNotifications || []).filter((notification) => {
      if (seen.has(notification.event_type)) return false;
      seen.add(notification.event_type);
      return true;
    });
  }, [emailNotifications]);

  const loadEmailNotifications = useCallback(async (nextApplicationId) => {
    if (!nextApplicationId) return;
    const result = await listExternalEmailNotifications(nextApplicationId);
    if (result.error) {
      console.warn('Unable to load ACC email notifications:', result.error.message);
      setEmailNotifications([]);
      return;
    }
    setEmailNotifications(result.data || []);
  }, []);

  const loadApplication = useCallback(async () => {
    if (!applicationId) {
      setIsLoading(false);
      setApplication(null);
      setComments([]);
      setEmailNotifications([]);
      setError('Application could not be loaded - please refresh and try again.');
      return;
    }

    const fallbackApplication = initialApplication?.id === applicationId ? initialApplication : null;
    if (fallbackApplication) {
      setApplication(fallbackApplication);
      setComments([]);
      setEmailNotifications([]);
    }

    setIsLoading(!fallbackApplication);
    setError('');

    let result = null;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      result = await getApplication(applicationId);
      if (!result.error && result.data?.application) break;
      if (!fallbackApplication || attempt === 3) break;
      await wait(1200);
    }

    setIsLoading(false);

    if (result?.error || !result?.data?.application) {
      if (fallbackApplication) {
        console.warn('ACC detail refresh pending:', result?.error?.message || 'Application not available yet');
        setApplication(fallbackApplication);
        setComments([]);
        setEmailNotifications([]);
        setError('');
        return;
      }

      setApplication(null);
      setComments([]);
      setEmailNotifications([]);
      setError('Application could not be loaded - please refresh and try again.');
      return;
    }

    const loadedApplication = result.data.application;
    setApplication(loadedApplication);
    setComments(result.data.comments || []);

    const att = await listACCAttachments(applicationId);
    setAttachments(att.error ? [] : (att.data || []));

    if (!loadedApplication.submitter_user_id && loadedApplication.submitter_email) {
      await loadEmailNotifications(applicationId);
    } else {
      setEmailNotifications([]);
    }
  }, [applicationId, initialApplication, loadEmailNotifications]);

  useEffect(() => {
    if (!isOpen) return;
    setApplication(null);
    setComments([]);
    setCommentBody('');
    setDecisionNote('');
    setEmailNotifications([]);
    setRetryingEmailId(null);
    setConfirmAction(null);
    setAttachments([]);
    setPreviewAttachment(null);
    setPreviewUrl('');
    setPreviewLoading(false);
    loadApplication();
  }, [applicationId, isOpen, loadApplication]);

  const handleRetryEmail = async (notification) => {
    if (!notification || retryingEmailId) return;
    setRetryingEmailId(notification.id);
    setError('');

    const result = await retryExternalEmailNotification({
      applicationId,
      eventType: notification.event_type,
    });

    setRetryingEmailId(null);
    if (result.error) {
      setError(result.error.message || 'Unable to retry email delivery.');
      return;
    }

    await loadEmailNotifications(applicationId);
    onChanged();
  };

  const handleOpenAttachment = async (attachment) => {
    setPreviewAttachment(attachment);
    setPreviewUrl('');
    setPreviewLoading(true);
    const { data: url, error: urlError } = await getACCAttachmentUrl(attachment.storage_path);
    setPreviewLoading(false);
    if (urlError || !url) {
      setPreviewAttachment(null);
      setError('Could not open attachment. Please try again.');
      return;
    }
    setPreviewUrl(url);
  };

  const closePreview = () => {
    setPreviewAttachment(null);
    setPreviewUrl('');
    setPreviewLoading(false);
  };

  const handleAddComment = async () => {
    if (!commentBody.trim() || isSaving) return;
    setIsSaving(true);
    setError('');
    const nextBody = commentBody.trim();
    const result = await addComment({
      applicationId,
      body: nextBody,
      hoaId: application?.hoa_id,
      applicationStatus: application?.status,
      projectTitle: application?.project_title,
      actorUserId: user?.id,
    });
    setIsSaving(false);

    if (result.error) {
      setError(result.error.message || 'Unable to add comment.');
      return;
    }

    const createdComment = {
      ...result.data,
      author_name:
        user?.full_name
        || user?.name
        || user?.email
        || 'You',
    };

    setComments((current) => (
      current.some((comment) => comment.id === createdComment.id)
        ? current
        : [...current, createdComment]
    ));
    setCommentBody('');
    loadApplication().then(null, (loadError) => {
      console.warn('ACC detail refresh after comment failed:', loadError.message || loadError);
    });
    Promise.resolve(onChanged?.({ showLoading: false, showErrors: false })).then(null, (changedError) => {
      console.warn('ACC list refresh after comment failed:', changedError.message || changedError);
    });
  };

  const handleDecision = async (decision) => {
    setIsSaving(true);
    setError('');
    const result = await decideApplication({ applicationId, decision, decisionNote });
    setIsSaving(false);
    setConfirmAction(null);

    if (result.error) {
      setError(result.error.message || 'Unable to record decision.');
      return;
    }

    await loadApplication();
    onChanged();
  };

  const handleArchive = async () => {
    setIsSaving(true);
    setError('');
    const result = await archiveApplication(applicationId);
    setIsSaving(false);
    setConfirmAction(null);

    if (result.error) {
      setError(result.error.message || 'Unable to archive application.');
      return;
    }

    await loadApplication();
    onChanged();
  };

  const handleDelete = async () => {
    setIsSaving(true);
    setError('');
    const result = await deleteApplication({
      applicationId,
      actorRole: role,
    });
    setIsSaving(false);
    setConfirmAction(null);

    if (result.error) {
      setError(result.error.message || 'Unable to delete application.');
      return;
    }

    if (onDeleted) {
      onDeleted(applicationId);
    } else {
      await onChanged?.({ showLoading: false, showErrors: false });
      onClose();
    }
  };

  if (!isOpen) return null;

  const previewKind = previewAttachment ? getPreviewKind(previewAttachment) : null;

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} className="w-full max-w-4xl" mobileMode="fullscreen">
      <div data-testid="acc-detail-modal" className="relative w-full max-h-[90vh] max-md:h-full max-md:max-h-none overflow-hidden bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30 flex flex-col">
        {confirmAction && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/95 rounded-2xl">
            <div className="p-8 max-w-sm w-full text-center">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4 ${
                ['denied', 'delete'].includes(confirmAction) ? 'bg-red-50' : 'bg-amber-50'
              }`}>
                <span className={`material-symbols-outlined ${
                  ['denied', 'delete'].includes(confirmAction) ? 'text-red-500' : 'text-amber-500'
                }`} style={{ fontSize: '24px' }}>warning</span>
              </div>
              <h3 className="font-bold text-primary-dark text-lg mb-2">
                {confirmAction === 'accepted' && 'Mark this application as Accepted?'}
                {confirmAction === 'denied' && 'Mark this application as Denied?'}
                {confirmAction === 'archive' && 'Archive this application?'}
                {confirmAction === 'delete' && 'Delete this ACC application?'}
              </h3>
              <p className="text-slate-500 text-sm font-medium mb-6">
                {(confirmAction === 'accepted' || confirmAction === 'denied') && 'This action sets the official decision.'}
                {confirmAction === 'archive' && 'Archived applications are read-only and move out of the active list.'}
                {confirmAction === 'delete' && 'This permanently removes the application and its internal comments. This cannot be undone.'}
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmAction(null)}
                  className="flex-1 border border-slate-200 text-slate-700 font-semibold rounded-full px-4 py-2.5 text-sm hover:bg-section-bg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (confirmAction === 'accepted') handleDecision('accepted');
                    else if (confirmAction === 'denied') handleDecision('denied');
                    else if (confirmAction === 'archive') handleArchive();
                    else if (confirmAction === 'delete') handleDelete();
                  }}
                  className={`flex-1 font-semibold rounded-full px-4 py-2.5 text-sm transition-colors text-white ${
                    ['denied', 'delete'].includes(confirmAction) ? 'bg-red-600 hover:bg-red-500' : 'bg-amber-600 hover:bg-amber-500'
                  }`}
                >
                  {confirmAction === 'accepted' && 'Accept'}
                  {confirmAction === 'denied' && 'Deny'}
                  {confirmAction === 'archive' && 'Archive'}
                  {confirmAction === 'delete' && 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

          <div className="sticky top-0 z-20 bg-white flex items-start justify-between gap-4 p-5 sm:p-6 border-b border-amber-100/30">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-primary-dark truncate">{application?.project_title || 'ACC Application'}</h2>
                {application?.status && <StatusPill status={application.status} />}
              </div>
              <p className="text-sm font-medium text-slate-500">Preliminary guidance is not formal board approval.</p>
            </div>
            <button data-testid="acc-detail-close" type="button" onClick={onClose} className="w-11 h-11 rounded-full border border-amber-100/40 text-primary-dark hover:bg-section-bg transition-all flex items-center justify-center flex-shrink-0">
              <MaterialIcon name="close" size={22} />
            </button>
          </div>

          <div className="overflow-y-auto p-5 sm:p-6 space-y-6">
            {isLoading ? (
              <div className="bg-section-bg rounded-[2.5rem] p-8 text-sm font-bold text-slate-500">Loading application...</div>
            ) : error && !application ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center">
                <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <MaterialIcon name="error" className="text-rose-700" size={24} />
                </div>
                <h3 className="text-lg font-bold text-primary-dark mb-2">Application unavailable</h3>
                <p className="text-sm font-medium text-rose-800 mb-6">{error}</p>
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center justify-center bg-secondary text-white px-6 py-3 rounded-full font-bold hover:brightness-110 active:scale-95 transition-all"
                >
                  Close
                </button>
              </div>
            ) : application ? (
              <>
                <section className="bg-section-bg rounded-[2.5rem] p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-2xl p-5 warm-tactile-shadow border border-amber-100/30">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Submitter</div>
                      <div className="text-lg font-bold text-primary-dark">{application.submitter_display_name}</div>
                      {application.submitter_email && <div className="text-sm font-medium text-slate-500 mt-1">{application.submitter_email}</div>}
                      {application.submitter_phone && <div className="text-sm font-medium text-slate-500 mt-1">{application.submitter_phone}</div>}
                    </div>
                    <div className="bg-white rounded-2xl p-5 warm-tactile-shadow border border-amber-100/30">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Property</div>
                      <div className="text-lg font-bold text-primary-dark">{application.property_address_snapshot || 'No property linked'}</div>
                      <div className="text-sm font-medium text-slate-500 mt-1">Submitted {formatDateTime(application.created_at)}</div>
                    </div>
                  </div>

                  {!application.submitter_user_id && application.submitter_email && latestEmailNotifications.length > 0 && (
                    <div className="mt-4 bg-white rounded-2xl p-5 warm-tactile-shadow border border-amber-100/30">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">External email delivery</div>
                      <div className="space-y-3">
                        {latestEmailNotifications.map((notification) => {
                          const label = emailEventLabels[notification.event_type] || 'Email';
                          const dateValue = notification.sent_at || notification.attempted_at || notification.created_at;
                          const isRetrying = retryingEmailId === notification.id;

                          return (
                            <div key={notification.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl bg-section-bg px-4 py-3">
                              <div className="flex items-start gap-3">
                                {notification.status === 'sent' && <MaterialIcon name="mark_email_read" className="text-emerald-700 mt-0.5" size={20} />}
                                {notification.status === 'failed' && <MaterialIcon name="error" className="text-rose-700 mt-0.5" size={20} />}
                                {notification.status === 'pending' && <MaterialIcon name="pending" className="text-slate-500 mt-0.5" size={20} />}
                                <div>
                                  {notification.status === 'sent' && (
                                    <p className="text-sm font-bold text-emerald-800">{label} sent to {notification.recipient_email}</p>
                                  )}
                                  {notification.status === 'failed' && (
                                    <p className="text-sm font-bold text-rose-800">Email delivery failed. {notification.error_message || 'No error detail returned.'}</p>
                                  )}
                                  {notification.status === 'pending' && (
                                    <p className="text-sm font-bold text-slate-600">Email queued...</p>
                                  )}
                                  <p className="text-xs font-bold text-slate-500 mt-1">{formatDateTime(dateValue)}</p>
                                </div>
                              </div>

                              {notification.status === 'failed' && permissions.canRetryEmail && (
                                <button
                                  type="button"
                                  disabled={isRetrying}
                                  onClick={() => handleRetryEmail(notification)}
                                  className="inline-flex items-center justify-center gap-2 border border-secondary text-secondary px-4 py-2 rounded-full text-xs font-bold hover:bg-secondary hover:text-white transition-all disabled:opacity-50"
                                >
                                  <MaterialIcon name="refresh" size={16} />
                                  {isRetrying ? 'Retrying' : 'Retry'}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </section>

                <section className="bg-white rounded-2xl p-6 warm-tactile-shadow border border-amber-100/30">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">Project description</div>
                  <p className="text-sm font-medium leading-relaxed text-primary-dark whitespace-pre-wrap">{application.project_description}</p>
                </section>

                {attachments.length > 0 && (
                  <section className="bg-white rounded-2xl p-6 warm-tactile-shadow border border-amber-100/30">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">Attachments</div>
                    <div className="space-y-2">
                      {attachments.map((a) => (
                        <button key={a.id} type="button" onClick={() => handleOpenAttachment(a)}
                          className="w-full flex items-center gap-3 p-3 bg-section-bg rounded-2xl hover:bg-section-bg/70 transition-colors text-left">
                          <span className="material-symbols-outlined text-secondary" style={{ fontSize: '22px' }}>description</span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm font-bold text-primary-dark truncate">{a.filename}</span>
                            <span className="block text-[11px] font-medium text-slate-500">{Math.ceil(a.size_bytes/1024)} KB</span>
                          </span>
                          <span className="material-symbols-outlined text-slate-400" style={{ fontSize: '20px' }}>download</span>
                        </button>
                      ))}
                    </div>
                  </section>
                )}

                {['accepted', 'denied', 'archived'].includes(application.status) && (
                  <section className="bg-white rounded-2xl p-6 warm-tactile-shadow border border-amber-100/30">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Official decision</div>
                      <StatusPill status={application.status} />
                    </div>
                    {application.decision_note && <p className="text-sm font-medium leading-relaxed text-primary-dark whitespace-pre-wrap mb-3">{application.decision_note}</p>}
                    <p className="text-xs font-bold text-slate-500">
                      Decided by {application.decided_by_name || 'Unknown'} at {formatDateTime(application.decided_at)}
                    </p>
                    {application.status === 'archived' && (
                      <p className="text-xs font-bold text-slate-500 mt-2">
                        Archived by {application.archived_by_name || 'Unknown'} at {formatDateTime(application.archived_at)}
                      </p>
                    )}
                  </section>
                )}

                <section className="bg-section-bg rounded-[2.5rem] p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
                      <MaterialIcon name="chat_bubble" className="text-secondary" size={22} />
                    </div>
                    <h3 className="text-lg font-bold text-primary-dark">Internal Discussion</h3>
                  </div>

                  {permissions.canSeeComments ? (
                    <div className="space-y-4">
                      {comments.length === 0 ? (
                        <div className="bg-white rounded-2xl p-6 border border-amber-100/30 text-sm font-medium text-slate-500">No internal comments yet.</div>
                      ) : (
                        comments.map((comment) => (
                          <div key={comment.id} className="bg-white rounded-2xl p-5 warm-tactile-shadow border border-amber-100/30">
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                              <div className="text-sm font-bold text-primary-dark">{comment.author_name}</div>
                              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{formatRelative(comment.created_at)}</div>
                            </div>
                            <p className="text-sm font-medium leading-relaxed text-primary-dark whitespace-pre-wrap">{comment.body}</p>
                          </div>
                        ))
                      )}

                      <div className="bg-white rounded-2xl p-5 border border-amber-100/30">
                        <textarea
                          data-testid="acc-comment-body"
                          value={commentBody}
                          onChange={(event) => setCommentBody(event.target.value)}
                          disabled={!permissions.canComment}
                          placeholder={permissions.canComment ? 'Add an internal comment...' : 'Archived applications are read-only.'}
                          className="w-full min-h-28 px-4 py-3 bg-white border border-amber-100/40 rounded-xl text-primary-dark placeholder-slate-400 text-base md:text-sm font-medium outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/10 transition-all disabled:bg-section-bg"
                        />
                        <div className="flex justify-end mt-3">
                          <button
                            type="button"
                            disabled={!permissions.canComment || !commentBody.trim() || isSaving}
                            onClick={handleAddComment}
                            data-testid="acc-add-comment"
                            className="w-full sm:w-auto bg-secondary text-white px-6 py-3 rounded-full font-bold hover:brightness-110 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Add Comment
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-2xl p-6 border border-amber-100/30 text-sm font-bold text-slate-500">Internal discussion is private.</div>
                  )}
                </section>

                {(permissions.canDecide || permissions.canArchive || permissions.canDelete) && (
                  <section className="bg-white rounded-2xl p-5 sm:p-6 warm-tactile-shadow border border-amber-100/30 max-md:sticky max-md:bottom-0 max-md:z-10">
                    {permissions.canDecide && (
                      <>
                        <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Decision note</label>
                        <textarea
                          data-testid="acc-decision-note"
                          value={decisionNote}
                          onChange={(event) => setDecisionNote(event.target.value)}
                          className="w-full min-h-24 px-4 py-3 bg-white border border-amber-100/40 rounded-xl text-primary-dark placeholder-slate-400 text-base md:text-sm font-medium outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/10 transition-all"
                          placeholder="Optional note shown with the official decision"
                        />
                        <div className="flex flex-col sm:flex-row gap-3 mt-4">
                          <button data-testid="acc-accept" type="button" disabled={isSaving} onClick={() => setConfirmAction('accepted')} className="w-full sm:w-auto bg-emerald-700 text-white px-6 py-3 rounded-full font-bold hover:brightness-110 active:scale-95 transition-all disabled:opacity-50">
                            Accept
                          </button>
                          <button data-testid="acc-deny" type="button" disabled={isSaving} onClick={() => setConfirmAction('denied')} className="w-full sm:w-auto bg-rose-700 text-white px-6 py-3 rounded-full font-bold hover:brightness-110 active:scale-95 transition-all disabled:opacity-50">
                            Deny
                          </button>
                        </div>
                      </>
                    )}

                    {permissions.canArchive && (
                      <div className="flex justify-end">
                        <button data-testid="acc-archive" type="button" disabled={isSaving} onClick={() => setConfirmAction('archive')} className="border border-secondary text-secondary px-6 py-3 rounded-full font-bold hover:bg-secondary hover:text-white transition-all disabled:opacity-50">
                          Archive
                        </button>
                      </div>
                    )}

                    {permissions.canDelete && (
                      <div className="flex justify-end mt-4 pt-4 border-t border-amber-100/30">
                        <button data-testid="acc-delete" type="button" disabled={isSaving} onClick={() => setConfirmAction('delete')} className="inline-flex items-center justify-center gap-2 border border-rose-300 text-rose-700 px-6 py-3 rounded-full font-bold hover:bg-rose-700 hover:text-white transition-all disabled:opacity-50">
                          <MaterialIcon name="delete" size={18} />
                          Delete
                        </button>
                      </div>
                    )}
                  </section>
                )}

                {error && (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-800">{error}</div>
                )}
              </>
            ) : null}
          </div>
        </div>
    </Modal>

    <Modal
      isOpen={!!previewAttachment}
      onClose={closePreview}
      className="w-full max-w-5xl"
      mobileMode="fullscreen"
      zIndex="z-[60]"
    >
      <div data-testid="acc-attachment-preview" className="relative w-full max-h-[92vh] max-md:h-full max-md:max-h-none overflow-hidden bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30 flex flex-col">
        <div className="sticky top-0 z-10 bg-white flex items-center justify-between gap-4 p-4 sm:p-5 border-b border-amber-100/30">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 bg-section-bg rounded-xl flex items-center justify-center flex-shrink-0">
              <MaterialIcon
                name={previewKind === 'image' ? 'image' : previewKind === 'pdf' ? 'picture_as_pdf' : 'description'}
                className="text-secondary"
                size={22}
              />
            </div>
            <div className="min-w-0">
              <p className="text-sm sm:text-base font-bold text-primary-dark truncate">
                {previewAttachment?.filename || 'Attachment'}
              </p>
              <p className="text-xs font-medium text-slate-500">
                {formatBytes(previewAttachment?.size_bytes)}
                {previewAttachment?.mime_type ? ` · ${previewAttachment.mime_type}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {previewUrl && (
              <a
                href={previewUrl}
                download={previewAttachment?.filename || undefined}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-secondary text-secondary text-xs font-bold hover:bg-secondary hover:text-white transition-all"
              >
                <MaterialIcon name="download" size={16} />
                Download
              </a>
            )}
            <button
              type="button"
              onClick={closePreview}
              className="w-11 h-11 rounded-full border border-amber-100/40 text-primary-dark hover:bg-section-bg transition-all flex items-center justify-center"
              aria-label="Close preview"
            >
              <MaterialIcon name="close" size={22} />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-auto bg-section-bg/40">
          {previewLoading && (
            <div className="flex items-center justify-center h-full py-24 text-sm font-bold text-slate-500">
              Loading preview…
            </div>
          )}
          {!previewLoading && previewUrl && previewKind === 'image' && (
            <div className="flex items-center justify-center p-4 sm:p-6">
              <img
                src={previewUrl}
                alt={previewAttachment?.filename || 'Attachment preview'}
                className="max-w-full max-h-[80vh] rounded-xl warm-tactile-shadow bg-white"
              />
            </div>
          )}
          {!previewLoading && previewUrl && previewKind === 'pdf' && (
            <iframe
              title={previewAttachment?.filename || 'Attachment preview'}
              src={previewUrl}
              className="w-full h-[80vh] max-md:h-full bg-white"
            />
          )}
          {!previewLoading && previewUrl && previewKind === 'other' && (
            <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-4 warm-tactile-shadow">
                <MaterialIcon name="description" className="text-secondary" size={32} />
              </div>
              <h3 className="text-lg font-bold text-primary-dark mb-2">Preview not available in browser</h3>
              <p className="text-sm font-medium text-slate-500 max-w-md mb-6">
                This file type can't be previewed here. Download it to view in the appropriate application.
              </p>
              <a
                href={previewUrl}
                download={previewAttachment?.filename || undefined}
                className="inline-flex items-center gap-2 px-6 py-3 bg-secondary text-white rounded-full font-bold hover:brightness-110 active:scale-95 transition-all"
              >
                <MaterialIcon name="download" size={18} />
                Download {previewAttachment?.filename || 'file'}
              </a>
            </div>
          )}
        </div>
      </div>
    </Modal>
    </>
  );
};

export default ACCApplicationDetailModal;
